/** Leaderboard — gamified scorecard: podium with photos, points bars, permission-gated awards. */
import { PersonChip } from "@/components/PersonChip";
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trophy, Star, Flame, Award, Zap, Crown, Heart, Sparkles, ShieldCheck, Medal, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { celebrate, getCelebrationConfig } from "@/lib/celebrate";
import LeaderboardConfig from "./LeaderboardConfig";

const RANGES = [
  { d: 7, label: "Last 7 days" },
  { d: 30, label: "Last 30 days" },
  { d: 90, label: "Last 90 days" },
  { d: 365, label: "Last year" },
];

const BADGE_META = {
  prolific:        { icon: Award,      label: "Prolific",        hint: "10+ ratings received",      color: "text-blue-500 bg-blue-500/10 border-blue-500/30" },
  workhorse:       { icon: Zap,        label: "Workhorse",       hint: "25+ ratings received",      color: "text-indigo-500 bg-indigo-500/10 border-indigo-500/30" },
  crowd_favorite:  { icon: Heart,      label: "Crowd Favorite",  hint: "4.5★+ avg with 5+ ratings", color: "text-pink-500 bg-pink-500/10 border-pink-500/30" },
  five_star_streak:{ icon: Sparkles,   label: "5★ Streak",       hint: "5+ perfect ratings",        color: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  on_fire:         { icon: Flame,      label: "On Fire",         hint: "Active 3+ days in a row",   color: "text-orange-500 bg-orange-500/10 border-orange-500/30" },
  dedicated:       { icon: ShieldCheck,label: "Dedicated",       hint: "Active 7+ days in a row",   color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" },
  quality_machine: { icon: Crown,      label: "Quality Machine", hint: "10+ ratings at 4★+",        color: "text-purple-500 bg-purple-500/10 border-purple-500/30" },
};

const RANK_STYLES = [
  "border-amber-500 bg-gradient-to-br from-amber-500/20 to-amber-500/5 text-amber-600",
  "border-slate-400 bg-gradient-to-br from-slate-400/20 to-slate-400/5 text-slate-500",
  "border-orange-600 bg-gradient-to-br from-orange-600/20 to-orange-600/5 text-orange-600",
];

export default function Leaderboard() {
  const { activeWorkspaceId } = useAuth();
  const { can, ready } = useWorkspacePerms();
  const [rows, setRows] = useState([]);
  // R120 · zoom dialog state — which row's sparkline was clicked
  const [zoomUser, setZoomUser] = useState(null);
  const [pointsByUser, setPointsByUser] = useState({});
  const [members, setMembers] = useState([]);
  const [presets, setPresets] = useState([]);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [awardTarget, setAwardTarget] = useState(null);   // {user} or "global"
  const [configOpen, setConfigOpen] = useState(false);

  const canAward = ready && can("points.award");
  const canConfig = ready && can("points.rules.manage");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [{ data: lb }, { data: pts }, { data: mems }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/leaderboard?days=${days}`),
        api.get(`/workspaces/${activeWorkspaceId}/points-totals`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/members`).catch(() => ({ data: [] })),
      ]);
      // Feb 2026 R113 · Leaderboard used to render only users who
      // received a STAR RATING in the last N days — an empty list for
      // workspaces that rely on SOP/goal/manual awards. Rebuild rows
      // from the union of every active workspace member + everyone
      // with any points, so no-one silently disappears. Rating stats
      // stay layered on top when present.
      const ratingByUid = {};
      (lb || []).forEach((r) => { ratingByUid[r.user_id] = r; });
      const pointsByUid = {};
      (pts || []).forEach((p) => { pointsByUid[p.user_id] = p; });
      const activeMems = (mems || []).filter((m) => m.status === "active" && m.user);
      const memberByUid = {};
      activeMems.forEach((m) => { memberByUid[m.user_id] = m; });
      // Build the union set of user_ids
      const uidSet = new Set([
        ...Object.keys(ratingByUid),
        ...Object.keys(pointsByUid),
        ...activeMems.map((m) => m.user_id),
      ]);
      const merged = Array.from(uidSet).map((uid) => {
        const r = ratingByUid[uid] || {};
        const p = pointsByUid[uid] || {};
        const m = memberByUid[uid] || {};
        return {
          user_id: uid,
          name: r.name || p.user?.name || m.user?.name || m.user?.email || "Unknown",
          email: r.email || p.user?.email || m.user?.email,
          avatar_data_url: r.avatar_data_url || p.user?.avatar_data_url || m.user?.avatar_data_url,
          avg_stars: r.avg_stars || 0,
          rating_count: r.rating_count || 0,
          high_stars: r.high_stars || 0,
          perfect_stars: r.perfect_stars || 0,
          streak_days: r.streak_days || 0,
          badges: r.badges || [],
          last_rated_at: r.last_rated_at || null,
          points_total: p.points_total || 0,
          points_events: p.events || 0,
          // R115 · 30-day sparkline series fed by /points-totals.
          trend_30d: p.trend_30d || [],
          trend_days: p.trend_days || [],
        };
      });
      // Primary sort: points_total desc. Tie-break: rating avg + count.
      merged.sort((a, b) => {
        if (b.points_total !== a.points_total) return b.points_total - a.points_total;
        if (b.avg_stars !== a.avg_stars) return b.avg_stars - a.avg_stars;
        return b.rating_count - a.rating_count;
      });
      setRows(merged);
      const map = {};
      (pts || []).forEach((p) => { map[p.user_id] = p.points_total; });
      setPointsByUser(map);
      setMembers(activeMems.map((m) => ({ user_id: m.user_id, ...m.user })));
      getCelebrationConfig().then((c) => setPresets(c?.award_presets || [])).catch(() => {});
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, days]);

  const top3 = rows.slice(0, 3);
  const maxPoints = useMemo(() => Math.max(1, ...Object.values(pointsByUser).map((v) => Math.abs(v))), [pointsByUser]);

  return (
    <>
      <PageHeader
        eyebrow="Gamification"
        title="Leaderboard"
        description="Team ranked by ratings, streaks, badges and points. Play fair, win big."
        testid="leaderboard-header"
      />
      <div className="px-4 sm:px-6 md:px-10 mb-4 flex flex-wrap items-center gap-2">
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="leaderboard-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => <SelectItem key={r.d} value={String(r.d)}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[10px] text-muted-foreground mr-auto">{rows.length} ranked</span>
        {/* Feb 2026 R114 · One-click refetch after admins award / deduct
            or after new SOP runs complete. Uses the same load() that
            unions ratings + points + members, so this always mirrors
            the latest state. */}
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={load} disabled={busy}
                data-testid="leaderboard-recompute-btn">
          {busy ? "Recomputing…" : "Recompute"}
        </Button>
        {canConfig && (
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setConfigOpen(true)} data-testid="leaderboard-config-btn">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Rules & celebrations
          </Button>
        )}
        <Button
          size="sm"
          className="rounded-none h-8 bg-amber-500 hover:bg-amber-600 text-white"
          onClick={() => canAward ? setAwardTarget("global") : toast.error("You don't have the 'Award points' permission")}
          disabled={!ready}
          title={canAward ? "Award or deduct points" : "Requires the points.award permission"}
          data-testid="leaderboard-award-btn"
        >
          <Trophy className="w-3.5 h-3.5 mr-1.5" /> Award points
        </Button>
      </div>
      <div className="px-4 sm:px-6 md:px-10 pb-8">
        <SopMvpWidget wsId={activeWorkspaceId} canAward={canAward} />
        {busy ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing ranked yet" description="As team members create review items and receive ratings, they'll appear here." icon={Trophy} testid="leaderboard-empty" />
        ) : (
          <>
            <Podium top3={top3} pointsByUser={pointsByUser} />

            <div className="border border-border mt-6" data-testid="leaderboard-table">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
                <div className="col-span-1">Rank</div>
                <div className="col-span-4">Player</div>
                <div className="col-span-1 text-center">Avg ★</div>
                <div className="col-span-1 text-center">Ratings</div>
                <div className="col-span-1 text-center">Streak</div>
                <div className="col-span-3">Score</div>
                <div className="col-span-1" />
              </div>
              {rows.map((r, i) => {
                const pts = pointsByUser[r.user_id] ?? 0;
                const barW = Math.min(100, Math.round((Math.abs(pts) / maxPoints) * 100));
                // R125 · Insert a slightly thick divider once, right
                // where the sign flips from positive → negative.
                const prevPts = i > 0 ? (pointsByUser[rows[i - 1].user_id] ?? 0) : null;
                const showZeroDivider = i > 0 && prevPts >= 0 && pts < 0;
                return (
                  <React.Fragment key={r.user_id}>
                    {showZeroDivider && (
                      <div className="border-b-[3px] border-rose-500/60 bg-rose-500/5 px-4 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-rose-700 flex items-center gap-2"
                           data-testid="leaderboard-zero-divider">
                        <span className="h-px flex-1 bg-rose-500/40" />
                        <span>Below zero — negative points</span>
                        <span className="h-px flex-1 bg-rose-500/40" />
                      </div>
                    )}
                    <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm hover:bg-muted/30 transition-colors" data-testid={`leaderboard-row-${r.user_id}`}>
                      <div className="col-span-1">
                        <span className={`inline-flex items-center justify-center w-8 h-8 border-2 font-black text-sm ${RANK_STYLES[i] || "border-border text-foreground/50"}`}>
                          {i < 3 ? <Medal className="w-4 h-4" /> : i + 1}
                        </span>
                      </div>
                      <div className="col-span-4 min-w-0 flex items-center gap-3">
                        {/* R125 · Larger avatar (48px vs 36px) so faces read bigger */}
                        <Avatar user={r} size={48} ring={i < 3} rank={i} />
                        <div className="min-w-0">
                          <div className="font-semibold truncate"><PersonChip userId={r.user_id} name={r.name} /></div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(r.badges || []).slice(0, 2).map((b) => <BadgePill key={b} type={b} />)}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1 text-center inline-flex items-center justify-center gap-1">
                        <Star className="w-3 h-3 fill-current text-amber-400" />
                        <span className="font-bold">{r.avg_stars}</span>
                      </div>
                      <div className="col-span-1 text-center">{r.rating_count}</div>
                      <div className="col-span-1 text-center">
                        {r.streak_days > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-500" title={`${r.streak_days}-day streak`}>
                            <Flame className="w-3.5 h-3.5" /> {r.streak_days}
                          </span>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </div>
                      <div className="col-span-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-black text-sm w-16 text-right ${pts >= 0 ? "text-amber-600 dark:text-amber-400" : "text-red-500"}`} data-testid={`leaderboard-points-${r.user_id}`}>
                            {pts >= 0 ? "+" : ""}{pts}
                          </span>
                          <div className="flex-1 h-2.5 bg-foreground/5">
                            <div className={`h-2.5 ${pts >= 0 ? "bg-gradient-to-r from-amber-500 to-amber-300" : "bg-red-500/60"}`} style={{ width: `${barW}%` }} />
                          </div>
                          {/* R115 · 30-day trend sparkline. Positive-only fill
                              with a light zero-line so streaks pop and slumps
                              are immediately visible. */}
                          <Sparkline series={r.trend_30d || []} days={r.trend_days || []} testid={`leaderboard-sparkline-${r.user_id}`}
                                     onExpand={() => setZoomUser(r)} />
                        </div>
                      </div>
                      <div className="col-span-1 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-none h-7 px-2 text-[10px]"
                          onClick={() => canAward ? setAwardTarget(r) : toast.error("You don't have the 'Award points' permission")}
                          disabled={!ready}
                          data-testid={`leaderboard-award-${r.user_id}`}
                          title={canAward ? "Award or deduct points" : "Requires the points.award permission"}
                        >
                          +/− pts
                        </Button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}

        {awardTarget && (
          <AwardPointsDialog
            workspaceId={activeWorkspaceId}
            target={awardTarget === "global" ? null : awardTarget}
            members={members}
            presets={presets}
            onClose={() => setAwardTarget(null)}
            onSaved={async () => { await load(); setAwardTarget(null); window.dispatchEvent(new Event("acq-refresh-score")); }}
          />
        )}
        {configOpen && <LeaderboardConfig workspaceId={activeWorkspaceId} onClose={() => setConfigOpen(false)} />}
        {zoomUser && (
          <SparklineZoomDialog
            wsId={activeWorkspaceId}
            user={zoomUser}
            onClose={() => setZoomUser(null)}
          />
        )}
      </div>
    </>
  );
}

// R115 · Tiny SVG sparkline. 30 daily points-delta ticks compressed into
// ~70×22px. Positive days rendered as amber, negatives as red, zero-line
// visible so slumps register instantly. Renders nothing if the series
// is empty (nothing to say → nothing to draw).
// R120 · Feb 2026 — Click-to-expand chart. Loads a fresh series from
// /users/{uid}/points-trend?days=<n> on open so the 90-day (and larger)
// window doesn't inflate the initial page load.
function SparklineZoomDialog({ wsId, user, onClose }) {
  const [days, setDays] = useState(90);
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true, data: null });
      try {
        const { data } = await api.get(`/workspaces/${wsId}/users/${user.user_id}/points-trend?days=${days}`);
        if (!cancelled) setState({ loading: false, data });
      } catch { if (!cancelled) setState({ loading: false, data: null }); }
    })();
    return () => { cancelled = true; };
  }, [wsId, user.user_id, days]);
  const data = state.data;
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}
         data-testid="sparkline-zoom-overlay">
      <div className="bg-background border-2 border-primary shadow-2xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()} data-testid="sparkline-zoom-dialog">
        <div className="border-b px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user.name} · Points trend</p>
            <p className="text-[10px] text-muted-foreground">Total in window: {data ? `${data.total >= 0 ? "+" : ""}${data.total} pts` : "…"}</p>
          </div>
          <div className="inline-flex border">
            {[30, 90, 180].map((d) => (
              <button key={d} type="button" onClick={() => setDays(d)}
                      className={`h-7 px-2 text-[11px] border-r last:border-r-0 ${d === days ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                      data-testid={`sparkline-zoom-days-${d}`}>{d}d</button>
            ))}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="sparkline-zoom-close">✕</button>
        </div>
        <div className="p-4">
          {state.loading ? (
            <div className="h-32 flex items-center justify-center text-xs text-muted-foreground">Loading…</div>
          ) : !data ? (
            <div className="h-32 flex items-center justify-center text-xs text-muted-foreground">No data</div>
          ) : (
            <BigSparkline series={data.series} days={data.days} />
          )}
        </div>
      </div>
    </div>
  );
}

// Larger version of the trend renderer for the zoom dialog. ~640×140,
// clickable dots with per-day tooltips, gridlines at zero and the
// series min/max.
function BigSparkline({ series, days }) {
  const width = 640;
  const height = 140;
  const pad = 20;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  if (!series || series.length < 2) return <div className="text-xs text-muted-foreground">Not enough data.</div>;
  const max = Math.max(1, ...series.map((v) => Math.abs(v)));
  const mid = pad + innerH / 2;
  const step = innerW / (series.length - 1);
  const points = series.map((v, i) => {
    const x = pad + i * step;
    const y = mid - (v / max) * (innerH / 2 - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} className="w-full h-auto" viewBox={`0 0 ${width} ${height}`} data-testid="sparkline-zoom-svg">
      <line x1={pad} y1={mid} x2={width - pad} y2={mid} stroke="currentColor" strokeOpacity="0.2" />
      <polyline points={points} fill="none" stroke="currentColor" className="text-amber-500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {series.map((v, i) => v === 0 ? null : (
        <circle key={i} cx={pad + i * step} cy={mid - (v / max) * (innerH / 2 - 4)} r="3"
                className={v > 0 ? "text-emerald-500" : "text-red-500"} fill="currentColor">
          <title>{`${days[i]}: ${v >= 0 ? "+" : ""}${v} pts`}</title>
        </circle>
      ))}
      <text x={pad} y={height - 4} className="text-[9px] fill-current text-muted-foreground">{days[0]}</text>
      <text x={width - pad} y={height - 4} textAnchor="end" className="text-[9px] fill-current text-muted-foreground">{days[days.length - 1]}</text>
    </svg>
  );
}

function Sparkline({ series, days = [], testid, width = 72, height = 22, onExpand }) {
  if (!series || series.length < 2) {
    return <div className="w-[72px]" data-testid={`${testid}-empty`} />;
  }
  const max = Math.max(1, ...series.map((v) => Math.abs(v)));
  const step = width / (series.length - 1);
  const mid = height / 2;
  const points = series.map((v, i) => {
    const x = i * step;
    const y = mid - (v / max) * (mid - 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const nonZero = series.some((v) => v !== 0);
  // R119 · Native SVG tooltip built by concatenating daily labels so
  // hovering the sparkline reveals per-day deltas without pulling in
  // any extra libs. Only recent-active days appear (zeroes are
  // trimmed) to keep the tooltip scannable.
  const tooltip = (() => {
    const lines = [];
    const total = series.reduce((a, b) => a + b, 0);
    lines.push(`30-day net: ${total >= 0 ? "+" : ""}${total} pts`);
    lines.push("");
    for (let i = 0; i < series.length; i++) {
      const v = series[i];
      if (!v) continue;
      const d = days[i] || `day-${i + 1}`;
      lines.push(`${d}: ${v >= 0 ? "+" : ""}${v}`);
    }
    if (lines.length === 2) lines.push("(no scoring days in the last 30)");
    return lines.join("\n");
  })();
  return (
    <svg width={width} height={height} className={`shrink-0 ${onExpand ? "cursor-pointer hover:opacity-80" : ""}`}
         data-testid={testid} role="img" aria-label={tooltip}
         onClick={onExpand}>
      <title>{tooltip + (onExpand ? "\n\nClick to expand →" : "")}</title>
      <line x1="0" y1={mid} x2={width} y2={mid} stroke="currentColor" strokeOpacity="0.15" strokeWidth="1" />
      {nonZero && (
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          className="text-amber-500 dark:text-amber-400"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {series.map((v, i) => v === 0 ? null : (
        <circle
          key={i}
          cx={i * step}
          cy={mid - (v / max) * (mid - 2)}
          r="1.6"
          className={v > 0 ? "text-emerald-500" : "text-red-500"}
          fill="currentColor"
        >
          <title>{`${days[i] || `day-${i + 1}`}: ${v >= 0 ? "+" : ""}${v} pts`}</title>
        </circle>
      ))}
    </svg>
  );
}

function Podium({ top3, pointsByUser }) {
  const order = [top3[1], top3[0], top3[2]].filter(Boolean);
  const heightFor = (rank) => (rank === 0 ? 210 : rank === 1 ? 155 : 122);
  const plate = [
    "from-amber-400/40 via-amber-500/20 to-amber-500/5 border-amber-500 shadow-[0_-6px_24px_-6px_rgba(245,158,11,0.55)]",
    "from-slate-300/40 via-slate-400/20 to-slate-400/5 border-slate-400 shadow-[0_-4px_18px_-6px_rgba(148,163,184,0.5)]",
    "from-orange-500/40 via-orange-600/20 to-orange-600/5 border-orange-600 shadow-[0_-4px_18px_-6px_rgba(234,88,12,0.5)]",
  ];
  return (
    // R125 · Extra top padding (`pt-14`) so the #1 crown never gets clipped
    // by the container. Also a richer radial-gradient backdrop with subtle
    // sparkle overlay for the "more glamorous" ask.
    <div className="mb-6 border border-border bg-gradient-to-b from-primary/8 via-transparent to-amber-500/10 pt-14 pb-8 px-6 relative overflow-hidden" data-testid="leaderboard-podium">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #f59e0b 0, transparent 32%), radial-gradient(circle at 80% 20%, #8b5cf6 0, transparent 32%), radial-gradient(circle at 50% 90%, #22d3ee 0, transparent 30%)" }}
      />
      {/* Faint diagonal sparkles */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 12px, currentColor 12px 13px)" }} />
      <div className="flex items-end justify-center gap-4 max-w-3xl mx-auto relative">
        {order.map((r) => {
          const actualRank = top3.indexOf(r);
          const pts = pointsByUser[r.user_id] ?? 0;
          return (
            <div key={r.user_id} className="flex flex-col items-center flex-1" data-testid={`podium-rank-${actualRank + 1}`}>
              <div className="relative">
                {actualRank === 0 && (
                  // R125 · Slightly larger crown + more headroom so it never clips.
                  <Crown className="absolute -top-11 left-1/2 -translate-x-1/2 w-11 h-11 text-amber-500 animate-bounce drop-shadow-[0_3px_10px_rgba(245,158,11,0.75)]"
                         style={{ animationDuration: "2s" }} />
                )}
                {/* Glow behind avatar */}
                {actualRank === 0 && (
                  <span aria-hidden className="absolute -inset-3 rounded-full bg-amber-400/30 blur-xl pointer-events-none" />
                )}
                <Avatar user={r} size={actualRank === 0 ? 100 : 76} ring rank={actualRank} />
              </div>
              <div className="mt-3 text-center min-w-0 max-w-full">
                <div className="font-bold truncate" style={{ fontFamily: "var(--font-heading)" }}><PersonChip userId={r.user_id} name={r.name} /></div>
                <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 border border-amber-500/40 bg-amber-500/10">
                  <Trophy className="w-3.5 h-3.5 text-amber-500" />
                  <span className="font-mono font-black text-lg text-amber-600 dark:text-amber-400">{pts >= 0 ? "+" : ""}{pts}</span>
                  <span className="text-[9px] text-foreground/50 font-semibold">PTS</span>
                </div>
                <div className="flex items-center justify-center gap-1 mt-1.5 text-xs text-foreground/60">
                  <Star className="w-3.5 h-3.5 fill-current text-amber-400" />
                  <b>{r.avg_stars}</b> · {r.rating_count} rating{r.rating_count === 1 ? "" : "s"}
                  {r.streak_days > 0 && <span className="inline-flex items-center gap-0.5 text-orange-500 font-bold ml-1"><Flame className="w-3 h-3" />{r.streak_days}</span>}
                </div>
                <div className="flex flex-wrap gap-1 justify-center mt-2">
                  {(r.badges || []).slice(0, 3).map((b) => <BadgePill key={b} type={b} />)}
                </div>
              </div>
              <div
                className={`mt-3 w-full border-t-4 bg-gradient-to-b ${plate[actualRank]} flex items-start justify-center font-black text-4xl pt-3`}
                style={{ fontFamily: "var(--font-heading)", height: heightFor(actualRank) }}
                aria-hidden
              >
                #{actualRank + 1}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Avatar({ user, size = 40, ring = false, rank = -1 }) {
  const initial = (user.name || "?").slice(0, 1).toUpperCase();
  // R125 · Rank-specific ring colours so the podium avatars pop.
  const ringCls = ring
    ? rank === 0
      ? "ring-4 ring-amber-500/80 shadow-[0_0_24px_-4px_rgba(245,158,11,0.7)]"
      : rank === 1
        ? "ring-4 ring-slate-400/70"
        : rank === 2
          ? "ring-4 ring-orange-600/70"
          : "ring-4 ring-amber-500/40"
    : "";
  return user.avatar_data_url ? (
    <img src={user.avatar_data_url} alt="" className={`rounded-full object-cover ${ringCls}`} style={{ width: size, height: size }} />
  ) : (
    <div className={`rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold ${ringCls}`}
      style={{ width: size, height: size, fontSize: size / 2.4, fontFamily: "var(--font-heading)" }}>
      {initial}
    </div>
  );
}

function BadgePill({ type }) {
  const meta = BADGE_META[type];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className={`chip text-[9px] inline-flex items-center gap-1 ${meta.color}`} title={`${meta.label} — ${meta.hint}`} data-testid={`badge-${type}`}>
      <Icon className="w-2.5 h-2.5" /> {meta.label}
    </span>
  );
}

function AwardPointsDialog({ workspaceId, target, members, presets, onClose, onSaved }) {
  const [userId, setUserId] = useState(target?.user_id || "");
  const [points, setPoints] = useState(5);
  const [reason, setReason] = useState("");
  const [ledger, setLedger] = useState([]);
  const [busy, setBusy] = useState(false);
  const targetName = target?.name || members.find((m) => m.user_id === userId)?.name;

  useEffect(() => {
    if (!workspaceId || !userId) { setLedger([]); return; }
    api.get(`/workspaces/${workspaceId}/points-ledger?user_id=${userId}&limit=20`)
      .then((r) => setLedger(Array.isArray(r.data) ? r.data : (r.data?.rows || []))).catch(() => setLedger([]));
  }, [workspaceId, userId]);

  const submit = async () => {
    if (!userId) { toast.error("Pick a team member"); return; }
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/points-award`, { user_id: userId, points: Number(points), reason: reason.trim() });
      // R93 · Deducted points shouldn't feel like a party — swap the confetti
      // for a gentle "oh no" toast with a downcast emoji. Positive awards
      // still fire the confetti celebration.
      if (Number(points) < 0) {
        toast.message(`😔 ${Math.abs(Number(points))} points deducted from ${targetName || "Teammate"}`, {
          description: reason.trim(),
        });
      } else {
        celebrate("points_awarded", { name: targetName || "Teammate", points: `+${points}`, reason: reason.trim() });
      }
      await onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't award points"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="award-points-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />{target ? `Adjust points · ${target.name}` : "Award points"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!target && (
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Team member *</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger className="h-10 rounded-none" data-testid="award-user-select"><SelectValue placeholder="Pick a teammate" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {presets.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="award-presets">
              {presets.map((p) => (
                <button
                  key={p.id}
                  className={`chip text-[10px] inline-flex items-center gap-1 border transition-colors ${reason === p.label ? "border-amber-500 bg-amber-500/15" : "border-border hover:border-amber-500/50"}`}
                  onClick={() => { setPoints(p.points); setReason(p.label); }}
                  data-testid={`award-preset-${p.id}`}
                >
                  {p.emoji} {p.label} <b className={p.points >= 0 ? "text-emerald-600" : "text-red-500"}>{p.points >= 0 ? "+" : ""}{p.points}</b>
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {[5, 10, -5, -10, 25, -25].map((q) => (
              <Button key={q} size="sm" variant={points === q ? "default" : "outline"} className="rounded-none h-9 text-xs" onClick={() => setPoints(q)} data-testid={`award-quick-${q}`}>
                {q > 0 ? `+${q}` : q}
              </Button>
            ))}
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Custom amount (signed)</Label>
            <Input type="number" value={points} onChange={(e) => setPoints(Number(e.target.value))} className="h-10 font-mono" data-testid="award-custom-amount" />
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Reason *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this adjustment?" className="h-10" data-testid="award-reason" />
          </div>
          {ledger.length > 0 && (
            <div className="border border-border max-h-40 overflow-auto" data-testid="award-ledger">
              <div className="label-eyebrow text-[10px] px-3 py-2 border-b border-border bg-muted/40">Recent points history</div>
              {ledger.map((l) => (
                <div key={l.id} className="px-3 py-2 border-b border-border text-xs flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="truncate">{l.reason || l.source_label}</div>
                    <div className="text-[10px]">{l.awarded_by_name} · {new Date(l.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className={`font-mono font-bold ${l.points >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {l.points >= 0 ? "+" : ""}{l.points}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim() || !userId} className="rounded-none bg-amber-500 hover:bg-amber-600 text-white" data-testid="award-submit">
            {points >= 0 ? `Award +${points} pts` : `Deduct ${Math.abs(points)} pts`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SopMvpWidget({ wsId, canAward }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!wsId) return;
    try {
      const [p, h] = await Promise.all([
        api.get(`/workspaces/${wsId}/sop-mvp/preview`),
        api.get(`/workspaces/${wsId}/sop-mvp/history`),
      ]);
      setData(p.data);
      setHistory(h.data || []);
    } catch (e) {
      // Silent: widget is optional — permission errors just hide the widget.
      setData(null);
    }
  };
  useEffect(() => { load(); }, [wsId]);

  const awardNow = async () => {
    if (!confirm("Award the current-month SOP MVP right now (before the 25th auto-run)? This is idempotent unless you force it.")) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/sop-mvp/award-now?force=true`);
      toast.success("MVP awarded!");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Award failed"));
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const { leaderboard = [], month_key, top_score } = data;
  const winners = leaderboard.filter((r) => r.score === top_score);
  const rest = leaderboard.filter((r) => r.score !== top_score);

  return (
    <div className="border-2 border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-4 mb-4" data-testid="sop-mvp-widget">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-amber-600" />
        <div className="label-eyebrow flex-1">SOP MVP · {month_key}</div>
        {history.length > 0 && (
          <span className="text-[10px] text-muted-foreground" data-testid="sop-mvp-history-count">{history.length} past award{history.length !== 1 ? "s" : ""}</span>
        )}
        {canAward && (
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px] border-amber-500/50" onClick={awardNow} disabled={busy || winners.length === 0} data-testid="sop-mvp-award-now">
            {busy ? "Awarding…" : "Award now"}
          </Button>
        )}
      </div>
      {leaderboard.length === 0 ? (
        <div className="text-xs text-muted-foreground">No SOP runs completed this month yet — first mover wins!</div>
      ) : (
        <div className="space-y-2">
          {winners.map((w) => (
            <div key={w.user_id} className="flex items-center gap-3 border border-amber-500/40 bg-white/50 dark:bg-black/20 p-2" data-testid={`sop-mvp-winner-${w.user_id}`}>
              <Crown className="w-4 h-4 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{w.user?.name || w.user?.email || w.user_id}</div>
                <div className="text-[10px] text-muted-foreground">
                  {w.runs} runs · avg speed <b>{w.avg_speed_score}/100</b> · on-time <b>{Math.round(w.on_time_rate * 100)}%</b>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-amber-700">{w.score}</div>
                <div className="text-[9px] text-muted-foreground uppercase tracking-widest">score</div>
              </div>
            </div>
          ))}
          {rest.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Runners-up ({rest.length})</summary>
              <div className="mt-2 space-y-1">
                {rest.slice(0, 5).map((r) => (
                  <div key={r.user_id} className="flex items-center gap-2 text-[11px] pl-4">
                    <span className="truncate flex-1">{r.user?.name || r.user?.email}</span>
                    <span className="font-mono text-muted-foreground">{r.runs} runs · {r.avg_speed_score}/100 · score {r.score}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

